import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Search,
  Shield,
  Mail,
  Globe,
} from "lucide-react";

type Lead = {
  id: string;
  company_name: string;
  company_number: string;
  incorporation_date: string | null;
  sic_codes: string | null;
  registered_address: string | null;
  director_names: string | null;
  flag_type: "Keyword Match" | "SIC Match" | null;
  website: string | null;
  linkedin_url: string | null;
  email: string | null;
  invite_status: "Not Contacted" | "Invited" | "Signed Up" | "Ignore";
  score: number | null;
  notes: string | null;
  created_at: string;
  root_domain: string | null;
  domain_confidence: number | null;
  email_confidence: number | null;
  email_source: string | null;
  enrichment_status: string;
  last_enriched_at: string | null;
};

type SortField = "incorporation_date" | "score" | "company_name";
type SortDir = "asc" | "desc";

const STATUS_COLORS: Record<Lead["invite_status"], string> = {
  "Not Contacted": "bg-muted text-muted-foreground",
  Invited: "bg-primary/20 text-primary",
  "Signed Up": "bg-secondary/20 text-secondary",
  Ignore: "bg-destructive/20 text-destructive",
};

const FLAG_COLORS: Record<string, string> = {
  "Keyword Match": "bg-accent/20 text-accent",
  "SIC Match": "bg-secondary/20 text-secondary",
};

export default function AdminAISourcing() {
  
  const qc = useQueryClient();

  const [sortField, setSortField] = useState<SortField>("incorporation_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Partial<Lead>>({});
  const [enrichingId, setEnrichingId] = useState<string | null>(null);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["ch_ai_leads", sortField, sortDir],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ch_ai_leads")
        .select("*")
        .order(sortField, { ascending: sortDir === "asc" });
      if (error) throw error;
      return data as Lead[];
    },
  });

  const pullMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated. Please sign in again.");
      const { data, error } = await supabase.functions.invoke("fetch-ch-ai-companies", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw new Error(error.message || JSON.stringify(error));
      return data;
    },
    onSuccess: (result) => {
      toast.success(`Pull complete, ${result.inserted} new leads added, ${result.skipped} duplicates skipped.`);
      qc.invalidateQueries({ queryKey: ["ch_ai_leads"] });
    },
    onError: (e) => toast.error(`Pull failed: ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, fields }: { id: string; fields: Partial<Lead> }) => {
      const { error } = await supabase
        .from("ch_ai_leads")
        .update(fields)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Lead updated.");
      qc.invalidateQueries({ queryKey: ["ch_ai_leads"] });
      setEditingId(null);
      setEditFields({});
    },
    onError: (e) => toast.error(`Update failed: ${e.message}`),
  });

  const inviteMutation = useMutation({
    mutationFn: async ({ leadId, recipientEmail, companyName, directorName }: {
      leadId: string;
      recipientEmail: string;
      companyName: string;
      directorName?: string | null;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/send-invite-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ leadId, recipientEmail, companyName, directorName }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      toast.success("Invite sent!");
      qc.invalidateQueries({ queryKey: ["ch_ai_leads"] });
    },
    onError: (e) => toast.error(`Invite failed: ${e.message}`),
  });

  const enrichMutation = useMutation({
    mutationFn: async (leadId: string) => {
      setEnrichingId(leadId);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("enrich-lead-email", {
        body: { leadId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (result) => {
      if (result.found) {
        toast.success("Email found and saved.");
      } else {
        toast.info("No email found for this company.");
      }
      qc.invalidateQueries({ queryKey: ["ch_ai_leads"] });
      setEnrichingId(null);
    },
    onError: (e) => {
      toast.error(`Enrichment failed: ${e.message}`);
      setEnrichingId(null);
    },
  });

  const enrichCompanyMutation = useMutation({
    mutationFn: async (leadId: string) => {
      setEnrichingId(leadId);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("enrich-company", {
        body: { leadId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (result) => {
      if (result.email) {
        toast.success(`Domain + email found: ${result.email}`);
      } else if (result.domain) {
        toast.info(`Domain found (${result.domain}) but no email.`);
      } else {
        toast.info("No domain or email found.");
      }
      qc.invalidateQueries({ queryKey: ["ch_ai_leads"] });
      setEnrichingId(null);
    },
    onError: (e) => {
      toast.error(`Company enrichment failed: ${e.message}`);
      setEnrichingId(null);
    },
  });

  const bulkEnrichMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("enrich-company", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (result) => {
      toast.success(`Bulk enrichment: ${result.domainsFound ?? 0} domains, ${result.emailsFound ?? 0} emails found out of ${result.total ?? 0} leads.`);
      qc.invalidateQueries({ queryKey: ["ch_ai_leads"] });
    },
    onError: (e) => toast.error(`Bulk enrichment failed: ${e.message}`),
  });

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field)
      return <ChevronUp className="w-3 h-3 opacity-30 inline ml-1" />;
    return sortDir === "asc" ? (
      <ChevronUp className="w-3 h-3 inline ml-1 text-primary" />
    ) : (
      <ChevronDown className="w-3 h-3 inline ml-1 text-primary" />
    );
  }

  const filtered = leads.filter(
    (l) =>
      l.company_name.toLowerCase().includes(search.toLowerCase()) ||
      l.company_number.toLowerCase().includes(search.toLowerCase())
  );

  const startEdit = (lead: Lead) => {
    setEditingId(lead.id);
    setEditFields({
      website: lead.website,
      linkedin_url: lead.linkedin_url,
      invite_status: lead.invite_status,
      notes: lead.notes,
    });
  };

  const saveEdit = (id: string) => {
    updateMutation.mutate({ id, fields: editFields });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/40 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-primary" />
            <div>
              <h1 className="font-display text-lg font-semibold text-foreground">
                Founder Intelligence
              </h1>
              <p className="text-xs text-muted-foreground">
                Internal Admin · AI Startup Sourcing
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9 w-64 h-9 bg-muted/40 border-border text-sm"
                placeholder="Search companies…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              onClick={() => bulkEnrichMutation.mutate()}
              disabled={bulkEnrichMutation.isPending}
              variant="outline"
              className="border-border text-foreground hover:bg-muted"
            >
              <Globe
                className={`w-4 h-4 mr-2 ${bulkEnrichMutation.isPending ? "animate-spin" : ""}`}
              />
              {bulkEnrichMutation.isPending ? "Enriching…" : "Bulk Enrich"}
            </Button>
            <Button
              size="sm"
              onClick={() => pullMutation.mutate()}
              disabled={pullMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${pullMutation.isPending ? "animate-spin" : ""}`}
              />
              {pullMutation.isPending ? "Pulling…" : "Run Pull"}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="max-w-[1600px] mx-auto px-6 py-4 flex gap-6 text-sm text-muted-foreground border-b border-border/50">
        <span>
          <span className="text-foreground font-semibold">{leads.length}</span> total leads
        </span>
        <span>
          <span className="text-foreground font-semibold">
            {leads.filter((l) => l.invite_status === "Not Contacted").length}
          </span>{" "}
          not contacted
        </span>
        <span>
          <span className="text-foreground font-semibold">
            {leads.filter((l) => l.invite_status === "Invited").length}
          </span>{" "}
          invited
        </span>
        <span>
          <span className="text-foreground font-semibold">
            {leads.filter((l) => l.invite_status === "Signed Up").length}
          </span>{" "}
          signed up
        </span>
        {search && (
          <span>
            Showing{" "}
            <span className="text-foreground font-semibold">{filtered.length}</span>{" "}
            results
          </span>
        )}
      </div>

      {/* Table */}
      <div className="max-w-[1600px] mx-auto px-6 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-muted-foreground">
            {leads.length === 0
              ? 'No leads yet. Click "Run Pull" to fetch from Companies House.'
              : "No results match your search."}
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-card/60 hover:bg-card/60">
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap"
                    onClick={() => toggleSort("company_name")}
                  >
                    Company <SortIcon field="company_name" />
                  </TableHead>
                  <TableHead>Number</TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap"
                    onClick={() => toggleSort("incorporation_date")}
                  >
                    Inc. Date <SortIcon field="incorporation_date" />
                  </TableHead>
                  <TableHead>Flag</TableHead>
                  <TableHead>SIC Codes</TableHead>
                  <TableHead>Directors</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap"
                    onClick={() => toggleSort("score")}
                  >
                    Score <SortIcon field="score" />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead>LinkedIn</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((lead) => {
                  const isEditing = editingId === lead.id;
                  return (
                    <TableRow
                      key={lead.id}
                      className="hover:bg-muted/20 transition-colors"
                    >
                      <TableCell className="font-medium text-foreground max-w-[200px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="truncate cursor-default">
                              {lead.company_name}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="overflow-visible whitespace-normal break-words text-sm font-medium" style={{ maxWidth: "min(600px, 90vw)" }}>
                            {lead.company_name}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {lead.company_number}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {lead.incorporation_date
                          ? new Date(lead.incorporation_date).toLocaleDateString(
                              "en-GB",
                              { day: "2-digit", month: "short", year: "numeric" }
                            )
                          : ", "}
                      </TableCell>
                      <TableCell>
                        {lead.flag_type ? (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${FLAG_COLORS[lead.flag_type] ?? ""}`}
                          >
                            {lead.flag_type}
                          </span>
                        ) : (
                          ", "
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[120px]">
                        <div className="truncate" title={lead.sic_codes ?? ""}>
                          {lead.sic_codes || ", "}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[160px]">
                        {lead.director_names ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="truncate cursor-default">
                                {lead.director_names}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="overflow-visible whitespace-normal break-words text-xs" style={{ maxWidth: "min(400px, 90vw)" }}>
                              {lead.director_names.split(";").map((d, i) => (
                                <div key={i} className="py-0.5">{d.trim()}</div>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          ", "
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[180px]">
                        <div
                          className="truncate"
                          title={lead.registered_address ?? ""}
                        >
                          {lead.registered_address || ", "}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                            (lead.score ?? 0) >= 5
                              ? "bg-primary/20 text-primary"
                              : (lead.score ?? 0) >= 3
                              ? "bg-secondary/20 text-secondary"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {lead.score ?? 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Select
                            value={editFields.invite_status ?? lead.invite_status}
                            onValueChange={(v) =>
                              setEditFields((f) => ({
                                ...f,
                                invite_status: v as Lead["invite_status"],
                              }))
                            }
                          >
                            <SelectTrigger className="h-7 text-xs w-36">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["Not Contacted", "Invited", "Signed Up", "Ignore"].map(
                                (s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[lead.invite_status]}`}
                          >
                            {lead.invite_status}
                          </span>
                        )}
                      </TableCell>
                      {/* Email column */}
                      <TableCell className="text-xs max-w-[180px]">
                        {lead.email ? (
                          <a
                            href={`mailto:${lead.email}`}
                            className="text-primary hover:underline truncate block"
                            title={lead.email}
                          >
                            {lead.email}
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">, </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[140px]">
                        {isEditing ? (
                          <Input
                            className="h-7 text-xs"
                            value={editFields.website ?? ""}
                            onChange={(e) =>
                              setEditFields((f) => ({
                                ...f,
                                website: e.target.value,
                              }))
                            }
                            placeholder="https://…"
                          />
                        ) : lead.website ? (
                          <a
                            href={lead.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate block"
                          >
                            {lead.website}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">, </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs max-w-[140px]">
                        {isEditing ? (
                          <Input
                            className="h-7 text-xs"
                            value={editFields.linkedin_url ?? ""}
                            onChange={(e) =>
                              setEditFields((f) => ({
                                ...f,
                                linkedin_url: e.target.value,
                              }))
                            }
                            placeholder="linkedin.com/in/…"
                          />
                        ) : lead.linkedin_url ? (
                          <a
                            href={lead.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate block"
                          >
                            LinkedIn
                          </a>
                        ) : (
                          <span className="text-muted-foreground">, </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        {isEditing ? (
                          <Textarea
                            className="text-xs min-h-[60px] resize-none"
                            value={editFields.notes ?? ""}
                            onChange={(e) =>
                              setEditFields((f) => ({
                                ...f,
                                notes: e.target.value,
                              }))
                            }
                            placeholder="Notes…"
                          />
                        ) : (
                          <div
                            className="text-xs text-muted-foreground truncate"
                            title={lead.notes ?? ""}
                          >
                            {lead.notes || ", "}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 min-w-[120px]">
                          {isEditing ? (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                className="h-7 text-xs px-3"
                                onClick={() => saveEdit(lead.id)}
                                disabled={updateMutation.isPending}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs px-2"
                                onClick={() => { setEditingId(null); setEditFields({}); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                                onClick={() => startEdit(lead)}
                              >
                                Edit
                              </Button>
                              {lead.invite_status !== "Invited" && lead.invite_status !== "Signed Up" && (
                                <>
                                  {lead.email ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs px-2 text-primary hover:text-primary hover:bg-primary/10"
                                      onClick={() =>
                                        inviteMutation.mutate({
                                          leadId: lead.id,
                                          recipientEmail: lead.email!,
                                          companyName: lead.company_name,
                                          directorName: lead.director_names,
                                        })
                                      }
                                      disabled={inviteMutation.isPending}
                                    >
                                      <Mail className="w-3 h-3 mr-1" />
                                      Invite
                                    </Button>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                                        onClick={() => enrichCompanyMutation.mutate(lead.id)}
                                        disabled={enrichingId === lead.id}
                                        title="Domain + email enrichment (Google CSE → Apollo → Hunter)"
                                      >
                                        {enrichingId === lead.id ? (
                                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                        ) : (
                                          <Globe className="w-3 h-3 mr-1" />
                                        )}
                                        Enrich
                                      </Button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
